import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";

const REVIEWS = [
  {
    name: "Andreas H.",
    role: "Frankfurt - Trading Betrug",
    result: "43.200 EUR zurückgeholt",
    text: "Die Kommunikation war professionell und transparent. Ich wurde nie im Dunkeln gelassen. Das Ergebnis hat mich sprachlos gemacht.",
    avatar: "AH",
  },
  {
    name: "Monika B.",
    role: "Düsseldorf - Festgeld Betrug",
    result: "75.000 EUR zurückgeholt",
    text: "Eine gefälschte Bank hatte mir ein Festgeldkonto verkauft. Nach Monaten der Verzweiflung hat das Team die Verantwortlichen gefunden und mein Kapital gesichert.",
    avatar: "MB",
  },
  {
    name: "Jürgen F.",
    role: "Leipzig - Forex Betrug",
    result: "28.000 EUR zurückgeholt",
    text: "Ich schämte mich sehr, auf den Betrug hereingefallen zu sein. Das Team war einfühlsam und diskret. Und sie haben geliefert.",
    avatar: "JF",
  },
  {
    name: "Petra K.",
    role: "Stuttgart - Investment Betrug",
    result: "52.500 EUR zurückgeholt",
    text: "Die Prüfung war gründlich und die nächsten Schritte waren jederzeit verständlich. Genau so braucht man Unterstützung in so einer Situation.",
    avatar: "PK",
  },
  {
    name: "Daniel R.",
    role: "Berlin - Fake Broker",
    result: "61.000 EUR zurückgeholt",
    text: "Schnell, klar und sehr strukturiert. Besonders gut war die Kombination aus technischer Analyse und rechtlicher Einordnung.",
    avatar: "DR",
  },
  {
    name: "Julia M.",
    role: "Hamburg - Krypto Scam",
    result: "31.400 EUR zurückgeholt",
    text: "Die Betreuung war diskret und sehr professionell. Ich hatte das Gefühl, dass mein Fall wirklich ernst genommen wurde.",
    avatar: "JM",
  },
];

function Stars() {
  return <span aria-hidden="true" className="text-gold">★★★★★</span>;
}

export function Reviews() {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const isFirstRender = useRef(true);

  const reviewCount = REVIEWS.length;
  const lastIndex = reviewCount - 1;

  const visibleRange = useMemo(() => {
    const start = Math.max(0, Math.min(activeIndex, reviewCount - 3));
    return [start, Math.min(reviewCount - 1, start + 2)];
  }, [activeIndex, reviewCount]);

  useEffect(() => {
    // Skip scrolling on initial render to prevent page jumping on load
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    const current = itemRefs.current[activeIndex];
    current?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  }, [activeIndex]);

  const goTo = (index: number) => {
    const nextIndex = Math.max(0, Math.min(index, lastIndex));
    setActiveIndex(nextIndex);
  };

  const prev = () => goTo(activeIndex - 1);
  const next = () => goTo(activeIndex + 1);

  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Erfahrungen unserer Mandanten</h2>
          <p className="mt-3 text-muted-foreground">
            Echte Rückmeldungen von Betroffenen, die wir in komplexen Betrugsfällen begleitet haben.
          </p>
        </div>

        <div className="mt-12 flex items-center gap-3 md:gap-4">
          <button
            type="button"
            onClick={prev}
            aria-label="Vorherige Bewertung"
            className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground transition-colors hover:border-gold hover:text-gold md:inline-flex"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>

          <div
            ref={scrollerRef}
            className="grid flex-1 grid-flow-col gap-4 overflow-x-auto pb-2 [scrollbar-width:none] md:gap-6 md:[grid-auto-columns:calc((100%-3rem)/3)] [grid-auto-columns:85%]"
          >
            {REVIEWS.map((review, index) => (
              <article
                key={review.name}
                ref={(node) => {
                  itemRefs.current[index] = node;
                }}
                className="flex h-full min-h-[260px] flex-col rounded-2xl border border-border bg-card p-5 md:p-6"
              >
                <div className="flex items-center gap-1 text-gold">
                  <Stars />
                </div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">
                  &quot;{review.text}&quot;
                </p>
                <p className="mt-6 text-sm font-semibold text-navy">{review.result}</p>
                <div className="mt-auto border-t border-border/70 pt-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-sm font-semibold text-primary-foreground">
                      {review.avatar}
                    </div>
                    <div>
                      <p className="font-semibold">{review.name}</p>
                      <p className="text-sm text-muted-foreground">{review.role}</p>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <button
            type="button"
            onClick={next}
            aria-label="Nächste Bewertung"
            className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground transition-colors hover:border-gold hover:text-gold md:inline-flex"
          >
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-8 flex items-center justify-center gap-2">
          {REVIEWS.map((review, index) => (
            <button
              key={review.name}
              type="button"
              onClick={() => goTo(index)}
              aria-label={`Bewertung ${index + 1}`}
              className={`h-2.5 rounded-full transition-all ${
                index === activeIndex ? "w-8 bg-navy" : "w-2.5 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>

      
        <p className="sr-only">
          Sichtbar sind aktuell die Bewertungen {visibleRange[0] + 1} bis {visibleRange[1] + 1} von {reviewCount}.
        </p>
      </div>
    </section>
  );
}